import { useEffect, useState } from "react";
import { Info, X } from "@phosphor-icons/react";
import api from "../lib/api";

/* FeatureInfoButton — small "i" icon that opens a modal with the editable
   feature description card (purpose / inputs / outputs / downloadable /
   shareable / needs_login). Content lives in chatbot_feature_info collection
   and is editable by admins. */
export default function FeatureInfoButton({ slug, className = "", testid }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    api.get(`/chatbot/feature-info/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, slug, data]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors ${className}`}
        title="What is this feature?"
        aria-label={`About ${slug}`}
        data-testid={testid || `feature-info-${slug}`}
      >
        <Info size={14} weight="duotone" />
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-background border hairline max-w-lg w-full p-5 space-y-4 max-h-[88vh] overflow-y-auto"
            data-testid={`feature-info-modal-${slug}`}>
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div>
                <div className="eyebrow">About this feature</div>
                <h3 className="font-serif text-2xl mt-1">{data?.title || slug}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="btn-ghost px-2 py-1.5 text-xs"
                data-testid={`feature-info-close-${slug}`} aria-label="Close">
                <X size={14} weight="bold" />
              </button>
            </div>
            {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
            {data?.error && (
              <div className="text-xs text-muted-foreground italic">
                This feature card isn't filled in yet. Admins can add one from the
                Control Room → Bot → Feature info.
              </div>
            )}
            {data && !data.error && (
              <div className="space-y-3 text-sm">
                {data.purpose && (
                  <Row label="Purpose" body={data.purpose} />
                )}
                {data.inputs && (
                  <Row label="What you enter" body={data.inputs} />
                )}
                {data.outputs && (
                  <Row label="What you get" body={data.outputs} />
                )}
                <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground pt-1 border-t hairline">
                  {data.downloadable && <Chip>Downloadable PDF</Chip>}
                  {data.shareable && <Chip>Shareable link (login)</Chip>}
                  {data.needs_login && <Chip>Free account required</Chip>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, body }) {
  return (
    <div>
      <div className="eyebrow mb-1">{label}</div>
      <div className="text-foreground text-xs whitespace-pre-wrap">{body}</div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border hairline bg-muted/30">
      {children}
    </span>
  );
}
